'use client';

import { forwardRef, useRef } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/components/language-provider';
import { useTranslation } from '@/hooks/useTranslation';
import { motion, useInView } from 'framer-motion';
import Image from 'next/image';
import { ArrowRight, MessageCircle, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── WhatsApp Chat Card (matches concept exactly) ────────────────────────────

function WhatsAppCard({ isRTL, locale, t }: { isRTL: boolean; locale: string; t: (key: string, params?: Record<string, string | number | boolean> & { returnObjects?: boolean }) => string }) {
  return (
    <div
      className="bg-white dark:bg-navy-800 rounded-2xl overflow-hidden border border-gray-100 dark:border-navy-700"
      style={{
        boxShadow:
          '0 20px 60px rgba(0,0,0,0.10), 0 4px 16px rgba(0,0,0,0.06)',
      }}
      role="presentation"
      aria-label="WhatsApp chat preview"
    >
      {/* Green accent bar */}
      <div className="bg-[#25D366] h-1" aria-hidden="true" />

      <div className="p-8">
        {/* Chat header */}
        <div className="flex items-center gap-3 mb-6 pb-5 border-b border-gray-100 dark:border-navy-700">
          <div
            aria-hidden="true"
            className="relative w-10 h-10 rounded-full overflow-hidden shrink-0 bg-white border border-gray-100 dark:border-navy-700"
          >
            <Image
              src="/images/logos/Logo.webp"
              alt=""
              fill
              sizes="40px"
              className="object-contain p-1"
            />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white leading-none mb-0.5">
              DentaWista
            </p>
            <p className="text-[11px] text-[#25D366] font-medium tracking-wide">
              ● Online now
            </p>
          </div>
          {/* WhatsApp logo */}
          <div className="ml-auto">
            <svg
              width="22"
              height="22"
              viewBox="0 0 32 32"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="16" cy="16" r="16" fill="#25D366" />
              <path
                d="M23.5 8.5C21.5 6.5 18.9 5.4 16.1 5.4C10.3 5.4 5.6 10.1 5.6 15.9C5.6 17.8 6.1 19.6 7 21.2L5.5 26.6L11.1 25.1C12.6 25.9 14.3 26.4 16.1 26.4C21.9 26.4 26.6 21.7 26.6 15.9C26.5 13.1 25.4 10.5 23.5 8.5ZM16.1 24.5C14.5 24.5 12.9 24 11.6 23.2L11.3 23L8 23.9L8.9 20.7L8.6 20.4C7.7 19 7.2 17.5 7.2 15.9C7.2 11 11.2 7 16.1 7C18.5 7 20.7 7.9 22.4 9.6C24.1 11.3 25 13.5 25 15.9C25 20.9 21 24.5 16.1 24.5ZM21 18.3C20.7 18.1 19.3 17.4 19.1 17.4C18.9 17.3 18.7 17.3 18.6 17.6C18.5 17.8 17.9 18.5 17.8 18.7C17.7 18.9 17.5 18.9 17.3 18.8C16.1 18.2 15.3 17.7 14.4 16.4C14.2 16.1 14.5 16.1 14.8 15.5C14.8 15.4 14.8 15.2 14.8 15.1C14.7 15 14.1 13.6 13.9 13.1C13.7 12.6 13.5 12.7 13.3 12.7H12.8C12.6 12.7 12.3 12.8 12.1 13C11.5 13.7 11 14.4 11 15.4C11 16.4 12.1 17.4 12.2 17.5C12.3 17.7 14.1 20.5 16.9 21.3C19.7 22.1 19.7 21.5 20.4 21.5C21.1 21.4 22.2 20.7 22.4 20C22.6 19.3 22.6 18.7 22.5 18.6C22.4 18.5 22.2 18.4 21 18.3Z"
                fill="white"
              />
            </svg>
          </div>
        </div>

        {/* "Chat with us" heading */}
        <p className="text-xs tracking-[0.2em] uppercase text-gray-400 dark:text-white/40 font-medium mb-4">
          {t('whatsapp.chatWithUs')}
        </p>

        {/* Chat bubbles */}
        <div className="flex flex-col gap-3 mb-6">
          {/* User bubble */}
          <div className="flex justify-end">
            <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-tr-sm bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-white text-sm leading-relaxed">
              {t('whatsapp.sampleUserMessage')}
            </div>
          </div>

          {/* Agent reply */}
          <div className="flex justify-start">
            <div
              className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-tl-sm text-white text-sm leading-relaxed"
              style={{
                background:
                  'linear-gradient(135deg, #25D366 0%, #1eba58 100%)',
              }}
            >
              {t('whatsapp.sampleAgentMessage')}
            </div>
          </div>
        </div>

        {/* Typing indicator */}
        <div
          className="flex items-center gap-1.5 mb-6"
          aria-hidden="true"
        >
          <div
            className="flex items-center gap-1 px-3.5 py-2 rounded-xl rounded-tl-sm"
            style={{ background: '#f0f0f0' }}
          >
            {[0, 0.2, 0.4].map((delay, i) => (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block"
                style={{
                  animation: `wa-bounce 1.2s ease-in-out ${delay}s infinite`,
                }}
              />
            ))}
          </div>
          <span className="text-[10px] text-gray-400 dark:text-white/30">
            typing&hellip;
          </span>
        </div>

        {/* Primary CTA */}
        <motion.a
          href="https://wa.me/905070808888"
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{
            scale: 1.02,
            boxShadow: '0 8px 28px rgba(37,211,102,0.35)',
          }}
          whileTap={{ scale: 0.98 }}
          className={cn(
            'flex items-center justify-center gap-2.5 w-full',
            'bg-[#25D366] text-white rounded-xl py-3.5 px-8',
            'text-base font-medium tracking-wide',
            'transition-all duration-300',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]/70'
          )}
          style={{ boxShadow: '0 4px 16px rgba(37,211,102,0.25)' }}
          aria-label="Open WhatsApp to start chatting"
        >
          <MessageCircle className="w-5 h-5" aria-hidden="true" />
          {t('whatsapp.startWhatsAppChat')}
        </motion.a>

        {/* Below CTA: phone + book online */}
        <div className="flex items-center justify-between mt-4">
          <a
            href="tel:+905070808888"
            className="text-sm text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/60 transition-colors duration-300 tracking-wide"
            aria-label="Call us"
          >
            +90 507 080 8888
          </a>
          <Link
            href={`/${locale}/book-appointment`}
            className="text-sm font-medium text-gold hover:text-gold-600 transition-colors duration-300 flex items-center gap-1"
            aria-label="Book online"
          >
            {t('whatsapp.orBookOnline')}
            <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const CtaSection = forwardRef<HTMLDivElement>((props, ref) => {
  const { isRTL } = useLanguage();
  const { t, locale } = useTranslation('common');

  const innerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(innerRef, { once: true, margin: '-60px' });

  const stagger = (i: number) => ({
    initial: { opacity: 0, y: 22 },
    animate: isInView
      ? { opacity: 1, y: 0 }
      : { opacity: 0, y: 22 },
    transition: {
      duration: 0.65,
      delay: i * 0.1,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  });

  const checkItemsRaw = t('cta.checkItems', { returnObjects: true });
  const checkItems = Array.isArray(checkItemsRaw)
    ? (checkItemsRaw as string[])
    : ['Free Consultation', 'No Commitment', 'Personalized Plan'];

  return (
    <section
      ref={ref}
      className="bg-[#faf8f4] dark:bg-navy-900 py-20 overflow-hidden"
      aria-label="WhatsApp Direct CTA"
    >
      <div
        ref={innerRef}
        className="max-w-6xl mx-auto px-6 lg:px-10"
      >
        {/* Main split grid */}
        <div
          className={cn(
            'grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center',
            isRTL && 'lg:[direction:rtl]'
          )}
        >
          {/* LEFT: Text content */}
          <div className={cn('flex flex-col gap-6', isRTL && 'text-right')}>
            {/* Eyebrow */}
            <motion.p
              {...stagger(0)}
              className="text-[10px] tracking-[0.4em] uppercase font-medium text-gold"
            >
              {t('cta.eyebrow')}
            </motion.p>

            {/* Headline */}
            <motion.h2
              {...stagger(1)}
              className="font-display text-4xl md:text-5xl font-light text-navy dark:text-white leading-tight tracking-tight"
            >
              {t('cta.headline')}
            </motion.h2>

            {/* Subtitle */}
            <motion.p
              {...stagger(2)}
              className="text-base text-navy/50 dark:text-white/50 leading-relaxed max-w-sm"
            >
              {t('cta.subheadline')}
            </motion.p>

            {/* Trust checklist */}
            <motion.ul
              {...stagger(3)}
              className="flex flex-col gap-3 mt-1"
              aria-label="Why choose us"
            >
              {checkItems.map((item, idx) => (
                <li
                  key={idx}
                  className={cn(
                    'flex items-center gap-3',
                    isRTL && 'flex-row-reverse'
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-navy"
                    style={{
                      background:
                        'linear-gradient(135deg, #D2A138 0%, #e2b04a 100%)',
                      boxShadow: '0 2px 8px rgba(210,161,56,0.35)',
                    }}
                  >
                    ✓
                  </span>
                  <span className="text-sm text-navy/65 dark:text-white/60 font-medium">
                    {item}
                  </span>
                </li>
              ))}
            </motion.ul>

            {/* Phone fallback */}
            <motion.a
              {...stagger(4)}
              href="tel:+905070808888"
              className={cn(
                'inline-flex items-center gap-2.5 mt-2 text-navy/40 dark:text-white/35 hover:text-navy/65 dark:hover:text-white/55 transition-colors duration-300 group w-fit',
                isRTL && 'flex-row-reverse'
              )}
              aria-label="Call us directly"
            >
              <Phone
                className="w-3.5 h-3.5 text-gold/60 group-hover:text-gold transition-colors duration-300"
                aria-hidden="true"
              />
              <span className="text-sm tracking-wide">+90 507 080 8888</span>
            </motion.a>
          </div>

          {/* RIGHT: WhatsApp chat card */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={
              isInView
                ? { opacity: 1, y: 0, scale: 1 }
                : { opacity: 0, y: 30, scale: 0.97 }
            }
            transition={{
              duration: 0.75,
              delay: 0.2,
              ease: [0.22, 1, 0.36, 1] as const,
            }}
          >
            <WhatsAppCard isRTL={isRTL} locale={locale} t={t} />
          </motion.div>
        </div>

        {/* Social proof footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 pt-8 border-t border-navy/[0.08] dark:border-white/[0.08] flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-1 text-center"
          aria-label="Social proof"
        >
          <span className="text-xs text-gray-400 dark:text-white/40">
            {t('cta.averageResponse')}&nbsp;
            <strong className="text-gray-600 dark:text-white/60 font-medium">
              {t('cta.responseTime')}
            </strong>
          </span>
          <span className="hidden sm:inline text-gray-300 dark:text-white/20 mx-3">
            &middot;
          </span>
          <span className="text-xs text-gray-400 dark:text-white/40">
            <strong className="text-gray-600 dark:text-white/60 font-medium">
              1,247
            </strong>
            &nbsp;{t('cta.consultationsThisMonth')}
          </span>
        </motion.div>
      </div>
    </section>
  );
});

CtaSection.displayName = 'CtaSection';

export default CtaSection;
